import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Square } from 'lucide-react';
import pizza from '../assets/voice/pizza.svg';
import pasta from '../assets/voice/pasta.svg';
import eggs from '../assets/voice/eggs.svg';
import cup from '../assets/voice/cup.svg';
import './VoiceRecordingOverlay.css';
import { microphoneIsQuiet, voiceBarLevel } from '../lib/voiceBars';

const illustrations = [
  { name: 'pizza', src: pizza, width: 96.812, height: 79.021 },
  { name: 'pasta', src: pasta, width: 166.594, height: 68.448 },
  { name: 'eggs', src: eggs, width: 139.010, height: 74.743 },
  { name: 'cup', src: cup, width: 106, height: 92 },
];

const VoiceRecordingOverlay = ({ isOpen, isRecording, isProcessing, onStop, onClose, audioStream }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const stopFiredRef = useRef(false);
  const [illustrationIndex, setIllustrationIndex] = useState(0);

  useEffect(() => {
    setIllustrationIndex(0);
    if (!isOpen || !isProcessing) return;
    const timer = window.setInterval(() => {
      setIllustrationIndex((index) => (index + 1) % illustrations.length);
    }, 1250);
    return () => window.clearInterval(timer);
  }, [isOpen, isProcessing]);

  useEffect(() => {
    if (isOpen && isRecording && !isProcessing) {
      stopFiredRef.current = false;
    }
  }, [isOpen, isRecording, isProcessing]);

  useEffect(() => {
    if (!isOpen || !isRecording || isProcessing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resizeCanvas();

    let audioContext;
    let analyser;
    let dataArray;
    let source;

    const setupAudio = async () => {
      try {
        if (!audioStream) return;
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.7;
        analyserRef.current = analyser;

        source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        dataArray = new Uint8Array(bufferLength);
        const samples = new Uint8Array(analyser.fftSize);

        const draw = () => {
          if (!isRecording) return;
          animationRef.current = requestAnimationFrame(draw);

          analyser.getByteFrequencyData(dataArray);
          analyser.getByteTimeDomainData(samples);
          const quiet = microphoneIsQuiet(samples);

          const rect = canvas.getBoundingClientRect();
          const width = rect.width;
          const height = rect.height;

          ctx.clearRect(0, 0, width, height);

          const barCount = 40;
          const barWidth = width / (barCount * 4);
          const gap = barWidth;
          const centerX = width / 2;
          const centerY = height / 2;

          ctx.lineCap = 'round';
          ctx.lineWidth = barWidth * 0.8;

          for (let i = 0; i < barCount; i++) {
            const dataIndex = Math.floor((i / barCount) * bufferLength * 0.7);
            const value = dataArray[dataIndex] || 0;
            const normalizedValue = voiceBarLevel(value, quiet);

            const minHeight = 4;
            const maxHeight = height * 0.7;
            const barHeight = minHeight + normalizedValue * (maxHeight - minHeight);

            const x = centerX + (i * (barWidth + gap));
            const mirrorX = centerX - (i * (barWidth + gap));

            ctx.strokeStyle = `rgba(81, 18, 10, ${0.4 + normalizedValue * 0.6})`;

            ctx.beginPath();
            ctx.moveTo(x, centerY - barHeight / 2);
            ctx.lineTo(x, centerY + barHeight / 2);
            ctx.stroke();

            if (i > 0) {
              ctx.beginPath();
              ctx.moveTo(mirrorX, centerY - barHeight / 2);
              ctx.lineTo(mirrorX, centerY + barHeight / 2);
              ctx.stroke();
            }
          }
        };

        draw();
      } catch (err) {
        console.error('Audio visualization error:', err);
      }
    };

    setupAudio();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [isOpen, isRecording, isProcessing, audioStream]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="voice-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-overlay-heading"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div className="voice-overlay__panel">
      <button
        className="voice-overlay__close"
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        aria-label="Close"
      >
        <X size={28} color="#51120A" style={{ pointerEvents: 'none' }} />
      </button>

      {isRecording && !isProcessing && (
        <div className="voice-overlay__stage">
          <h2 id="voice-overlay-heading" className="voice-overlay__heading">
            Tell us what<br />you’re making!
          </h2>

          <canvas
            ref={canvasRef}
            className="voice-overlay__waveform"
            role="img"
            aria-label="Live recording waveform"
          />

          <button
            className="voice-overlay__stop"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!stopFiredRef.current) {
                stopFiredRef.current = true;
                onStop();
              }
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!stopFiredRef.current) {
                stopFiredRef.current = true;
                onStop();
              }
            }}
            aria-label="Stop recording"
          >
            <Square size={24} color="#F3EEE4" fill="#F3EEE4" style={{ pointerEvents: 'none' }} />
          </button>
        </div>
      )}

      {isProcessing && (
        <div className="voice-overlay__stage voice-overlay__stage--processing" role="status" aria-live="polite">
          <h2 id="voice-overlay-heading" className="voice-overlay__heading">
            We’re working on<br />your recipe
          </h2>
          <div className="voice-overlay__illustrations" aria-hidden="true">
            {illustrations.map((illustration, index) => (
              <img
                key={illustration.name}
                src={illustration.src}
                alt=""
                data-illustration={illustration.name}
                className={`voice-overlay__illustration${index === illustrationIndex ? ' voice-overlay__illustration--active' : ''}`}
                width={illustration.width}
                height={illustration.height}
              />
            ))}
          </div>
        </div>
      )}
      </div>
    </div>,
    document.body
  );
};

export default VoiceRecordingOverlay;
